import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '@/shared/components';
import { useFormSubmission } from '@/shared/hooks/useFormSubmission';
import { DoctorForm } from '@/features/doctors/DoctorForm';
import { doctorsApi } from '@/shared/api';
import type { DoctorFormData } from '@/shared/schemas';

/**
 * Страница создания нового врача
 * Содержит форму для добавления врача в систему
 */
export const DoctorCreatePage: React.FC = () => {
  const navigate = useNavigate();

  /**
   * Хук для управления отправкой формы с уведомлениями
   */
  const { isSubmitting, handleSubmit } = useFormSubmission<DoctorFormData>({
    onSubmit: async (data: DoctorFormData) => {
      // Prepare data for API, handling optional properties properly
      const apiData = {
        fullName: data.fullName,
        specialization: data.specialization,
        description: data.description,
        educationPrimary: data.educationPrimary,
        educationAdvanced: data.educationAdvanced,
        interestsArea: data.interestsArea,
        principles: data.principles,
        experience: data.experience,
        isActive: data.isActive,
        isAvailableOnline: data.isAvailableOnline,
        isAvailableAtHome: data.isAvailableAtHome,
        isAvailableInTheOffice: data.isAvailableInTheOffice,
        sortOrder: data.sortOrder,
        proDoctorovReviewsWidgetCode: data.proDoctorovReviewsWidgetCode || null,
        ...(data.slug && { slug: data.slug }),
        onSiteBookingUrl: data.onSiteBookingUrl || null,
      };
      const response = await doctorsApi.create(apiData);

      // Перенаправляем на страницу редактирования созданного врача через 2 секунды
      setTimeout(() => {
        navigate(`/doctors/${response.doctor.id}/edit`);
      }, 2000);
    },
    successMessage: 'Врач успешно создан!',
    errorMessage: 'Ошибка при создании врача'
  });

  return (
    <Layout title="Добавить врача">
      <div className="space-y-6">
        {/* Навигация */}
        <div className="flex items-center gap-2 text-sm">
          <Link to="/doctors" className="text-primary hover:text-primary/80">
            Врачи
          </Link>
          <i className="fas fa-chevron-right text-base-content/40"></i>
          <span className="text-base-content/60">Добавить врача</span>
        </div>

        {/* Заголовок */}
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-2">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary/80 rounded-xl flex items-center justify-center">
                <i className="fas fa-user-plus text-white"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-base-content">
                  Добавить нового врача
                </h2>
                <p className="text-base-content/60 mt-1">
                  Заполните информацию о враче для добавления в систему
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Форма создания врача */}
        <DoctorForm
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
          submitText="Создать врача"
        />

        {/* Дополнительные действия */}
        <div className="card card-bordered bg-base-200/50 shadow-sm">
          <div className="card-body p-2">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-base-content">Что дальше?</h3>
                <p className="text-sm text-base-content/60 mt-1">
                  После создания врача вы сможете загрузить фотографии и
                  настроить дополнительные параметры
                </p>
              </div>
              <Link to="/doctors" className="btn btn-ghost">
                <i className="fas fa-arrow-left mr-2"></i>
                Вернуться к списку
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
